<template>
  <div v-if="privilage() == 'admin'" class="dashboard">
    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h1>Tableau de bord</h1>
        <p class="page-subtitle">Vue d'ensemble du mois en cours</p>
      </div>
    </div>

    <!-- KPI Cards -->
    <div class="kpi-grid">
      <div class="kpi-card kpi-blue">
        <div class="kpi-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
        </div>
        <div class="kpi-body">
          <div class="kpi-value">{{ totalStaff }}</div>
          <div class="kpi-label">Total Employés</div>
        </div>
        <div class="kpi-trend">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>
        </div>
      </div>

      <div class="kpi-card kpi-purple">
        <div class="kpi-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
        </div>
        <div class="kpi-body">
          <div class="kpi-value">{{ avgWorkingHours }}<span class="kpi-unit">hrs</span></div>
          <div class="kpi-label">Moy. Heures / Jour</div>
        </div>
      </div>

      <div class="kpi-card kpi-green">
        <div class="kpi-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>
        </div>
        <div class="kpi-body">
          <div class="kpi-value">{{ totalAdvances }}<span class="kpi-unit">DH</span></div>
          <div class="kpi-label">Total Avances</div>
        </div>
      </div>
    </div>

    <!-- Lists Grid -->
    <div class="lists-grid">
      <!-- Most Absent -->
      <div class="list-card">
        <div class="list-head">
          <div class="list-icon list-icon-red">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          </div>
          <h3>Top Absences</h3>
          <span class="list-badge badge-danger">Ce mois</span>
        </div>
        <div class="list-body">
          <div v-if="mostAbsent && mostAbsent.length">
            <div v-for="(item, i) in mostAbsent" :key="item.staff_id" class="list-row">
              <div class="list-rank">{{ i + 1 }}</div>
              <div class="list-info">
                <div class="list-avatar">{{ item.first_name?.[0] }}{{ item.last_name?.[0] }}</div>
                <span class="list-name">{{ item.first_name }} {{ item.last_name }}</span>
              </div>
              <span class="list-value value-red">{{ item.absences }} {{ item.absences > 1 ? 'jours' : 'jour' }}</span>
            </div>
          </div>
          <div v-else class="list-empty">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01"/></svg>
            Aucune donnée disponible
          </div>
        </div>
      </div>

      <!-- Top Advances -->
      <div class="list-card">
        <div class="list-head">
          <div class="list-icon list-icon-green">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>
          </div>
          <h3>Top Avances</h3>
          <span class="list-badge badge-success">Ce mois</span>
        </div>
        <div class="list-body">
          <div v-if="topAdvances && topAdvances.length">
            <div v-for="(item, i) in topAdvances" :key="item.staff_id" class="list-row">
              <div class="list-rank">{{ i + 1 }}</div>
              <div class="list-info">
                <div class="list-avatar">{{ item.first_name?.[0] }}{{ item.last_name?.[0] }}</div>
                <span class="list-name">{{ item.first_name }} {{ item.last_name }}</span>
              </div>
              <span class="list-value value-green">{{ item.total_avance }} DH</span>
            </div>
          </div>
          <div v-else class="list-empty">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01"/></svg>
            Aucune donnée disponible
          </div>
        </div>
      </div>

      <!-- Top Remarks -->
      <div class="list-card">
        <div class="list-head">
          <div class="list-icon list-icon-amber">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
          </div>
          <h3>Top Remarques</h3>
          <span class="list-badge badge-warning">Ce mois</span>
        </div>
        <div class="list-body">
          <div v-if="topRemarks && topRemarks.length">
            <div v-for="(item, i) in topRemarks" :key="item.staff_id" class="list-row">
              <div class="list-rank">{{ i + 1 }}</div>
              <div class="list-info">
                <div class="list-avatar">{{ item.first_name?.[0] }}{{ item.last_name?.[0] }}</div>
                <span class="list-name">{{ item.first_name }} {{ item.last_name }}</span>
              </div>
              <span class="list-value value-amber">{{ item.total_remarks }} notes</span>
            </div>
          </div>
          <div v-else class="list-empty">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01"/></svg>
            Aucune donnée disponible
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Non-admin view -->
  <div v-else class="welcome-view">
    <div class="welcome-icon">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 12L12 3l9 9M5 10v9a1 1 0 001 1h4v-5h4v5h4a1 1 0 001-1v-9"/></svg>
    </div>
    <h2>Bienvenue</h2>
    <p>Sélectionnez un poste depuis le menu pour commencer.</p>
  </div>
</template>

<script>
export default {
  inject: ['privilage'],
  name: 'DashboardStatistics',
  data() {
    return {
      baseUrl: '',
      totalStaff: 0,
      avgWorkingHours: 0,
      totalAdvances: 0,
      mostAbsent: [],
      topAdvances: [],
      topRemarks: [],
      loading: true,
      retryCount: 0,
      maxRetries: 3,
    };
  },
  computed: {
    currentMonth() {
      return new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    }
  },
  methods: {
    async fetchStats() {
      this.loading = true;
      this.retryCount = 0;
      const getJSON = async (url) => {
        const separator = url.includes('?') ? '&' : '?';
        const res = await fetch(this.baseUrl + `${url}${separator}_=${Date.now()}`, {
          credentials: 'include',
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache',
          },
        });
        if (!res.ok) throw new Error(`Failed ${url}`);
        return res.json();
      };
      try {
        const [staff, hours, advances, absent, topAdv, remarks] = await Promise.all([
          getJSON(`/api-pointage/stats/total-staff`),
          getJSON(`/api-pointage/stats/avg-working-hours`),
          getJSON(`/api-pointage/stats/total-advances`),
          getJSON(`/api-pointage/stats/most-absent`),
          getJSON(`/api-pointage/stats/top-advances`),
          getJSON(`/api-pointage/stats/top-remarks`),
        ]);
        
        this.totalStaff = staff?.total || 0;
        this.avgWorkingHours = parseFloat((hours?.average || 0).toFixed(2));
        this.totalAdvances = advances?.total || 0;
        this.mostAbsent = Array.isArray(absent) ? absent : [];
        this.topAdvances = Array.isArray(topAdv) ? topAdv : [];
        this.topRemarks = Array.isArray(remarks) ? remarks : [];
        
        console.log('Stats loaded:', {
          staff: this.totalStaff,
          hours: this.avgWorkingHours,
          advances: this.totalAdvances
        });
      } catch (err) {
        console.error('Stats error:', err);
        // Retry after a delay
        if (this.retryCount < this.maxRetries) {
          this.retryCount++;
          setTimeout(() => {
            this.fetchStats();
          }, 2000);
        }
      } finally {
        this.loading = false;
      }
    }
  },
  mounted() {
    if (typeof this.privilage === 'function' && (this.privilage() === 'admin')) {
      setTimeout(() => {
        this.fetchStats();
      }, 500);
    }
  }
};
</script>

<style scoped>
.dashboard {
  display: flex;
  flex-direction: column;
  gap: 14px;
  animation: slideUp 0.25s ease;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.page-header h1 { font-size: 24px; margin-bottom: 3px; }
.page-subtitle { font-size: 13px; color: var(--gray-500); }

.header-date {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 8px 16px;
  background: var(--white);
  border: 1.5px solid var(--border);
  border-radius: var(--radius-sm);
  font-size: 13px; font-weight: 600;
  color: var(--gray-600);
  text-transform: capitalize;
}
.header-date svg { width: 15px; height: 15px; color: var(--primary); }

/* KPI */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.kpi-card {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 13px 16px;
  display: flex;
  align-items: center;
  gap: 11px;
  box-shadow: var(--shadow-sm);
  position: relative;
  overflow: hidden;
  transition: border-color var(--transition), box-shadow var(--transition);
}

.kpi-card:hover { border-color: var(--border-2); box-shadow: var(--shadow-md); }

.kpi-icon {
  width: 34px; height: 34px;
  border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.kpi-icon svg { width: 17px; height: 17px; }
.kpi-blue .kpi-icon { background: var(--primary-glow); color: var(--primary); }
.kpi-purple .kpi-icon { background: rgba(124,58,237,0.1); color: #7c3aed; }
.kpi-green .kpi-icon { background: var(--success-bg); color: var(--success); }

.kpi-body { flex: 1; }
.kpi-value {
  font-family: 'Arial', sans-serif;
  font-size: 24px; font-weight: 900;
  color: var(--gray-900);
  line-height: 1;
  margin-bottom: 4px;
}
.kpi-unit {
  font-size: 13px; font-weight: 700;
  color: var(--gray-500);
  margin-left: 4px;
}
.kpi-label { font-size: 13px; color: var(--gray-500); font-weight: 500; }

.kpi-trend {
  color: var(--success);
  opacity: 0.7;
}
.kpi-trend svg { width: 18px; height: 18px; }

/* Lists */
.lists-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.list-card {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}

.list-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-bottom: 1px solid var(--border);
}
.list-head h3 {
  flex: 1;
  font-size: 14px;
  color: var(--gray-800);
}

.list-icon {
  width: 28px; height: 28px;
  border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.list-icon svg { width: 14px; height: 14px; }
.list-icon-red { background: var(--danger-bg); color: var(--danger); }
.list-icon-green { background: var(--success-bg); color: var(--success); }
.list-icon-amber { background: var(--warning-bg); color: var(--warning); }

.list-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: var(--radius-full);
  font-size: 10.5px; font-weight: 700;
  letter-spacing: 0.04em;
}
.badge-danger { background: var(--danger-bg); color: var(--danger); }
.badge-success { background: var(--success-bg); color: var(--success); }
.badge-warning { background: var(--warning-bg); color: var(--warning); }

.list-body { padding: 8px 0; }

.list-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  transition: background var(--transition);
}
.list-row:hover { background: var(--gray-50); }

.list-rank {
  width: 20px;
  font-size: 12px; font-weight: 700;
  color: var(--gray-400);
  flex-shrink: 0;
}

.list-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.list-avatar {
  width: 28px; height: 28px;
  background: var(--primary-glow);
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 10px; font-weight: 800;
  color: var(--primary);
  flex-shrink: 0;
  text-transform: uppercase;
}

.list-name {
  font-size: 13px; font-weight: 500;
  color: var(--gray-700);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}

.list-value {
  font-size: 12.5px; font-weight: 700;
  flex-shrink: 0;
}
.value-red { color: var(--danger); }
.value-green { color: var(--success); }
.value-amber { color: var(--warning); }

.list-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 28px 20px;
  color: var(--gray-400);
  font-size: 13px;
}
.list-empty svg { width: 28px; height: 28px; opacity: 0.5; }

.welcome-view {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 16px;
  text-align: center;
}
.welcome-icon {
  width: 80px; height: 80px;
  background: var(--primary-glow);
  border-radius: 20px;
  display: flex; align-items: center; justify-content: center;
  color: var(--primary);
}
.welcome-icon svg { width: 36px; height: 36px; }
.welcome-view h2 { font-size: 24px; }
.welcome-view p { color: var(--gray-500); font-size: 15px; }

@media (max-width: 1100px) {
  .kpi-grid { grid-template-columns: repeat(3, 1fr); }
  .lists-grid { grid-template-columns: 1fr; }
}

@media (max-width: 768px) {
  .kpi-grid { grid-template-columns: 1fr; }
  .page-header { flex-direction: column; align-items: flex-start; }
  .header-date { width: 100%; }
}
</style>
